import type { SearchPropertiesResponseDatagramBuf } from "~/classes/generated/response"

import { DatagramBuf, DatagramErrorTypeBuf } from "~/classes/generated/datagram"
import { RequestDatagramBuf, SearchPropertiesRequestDatagramBuf } from "~/classes/generated/request"
import { StaticSearch } from "~/classes/search/StaticSearch"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId } from "~/utils/flatbuffers/datagram"
import { addEntityPropertySelectionToBuf } from "~/utils/flatbuffers/entity"
import { addSearchIdToBuf, parseSearchId } from "~/utils/flatbuffers/id"
import { parseSearch } from "~/utils/flatbuffers/search"
import { maybe } from "~/utils/helpers"

const console = useLogger("search/properties")

export function sendSearchPropertiesRequests(requests: SearchPropertiesRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const fbb = new DatagramBufferBuilder(64)
  const propertiesRequests = []
  for (const request of requests) {
    const selection = addEntityPropertySelectionToBuf(fbb, request.selection)
    const requestId = addRequestIdToBuf(fbb, request.requestId)
    const searchId = addSearchIdToBuf(fbb, request.searchId)
    SearchPropertiesRequestDatagramBuf.startSearchPropertiesRequestDatagramBuf(fbb)
    SearchPropertiesRequestDatagramBuf.addRequestId(fbb, requestId)
    SearchPropertiesRequestDatagramBuf.addOffline(fbb, request.offline)
    SearchPropertiesRequestDatagramBuf.addSearchId(fbb, searchId)
    SearchPropertiesRequestDatagramBuf.addSelection(fbb, selection)
    const propertiesRequest = SearchPropertiesRequestDatagramBuf.endSearchPropertiesRequestDatagramBuf(fbb)

    RequestDatagramBuf.startRequestDatagramBuf(fbb)
    RequestDatagramBuf.addSearchPropertiesRequest(fbb, propertiesRequest)
    propertiesRequests.push(RequestDatagramBuf.endRequestDatagramBuf(fbb))
  }

  const properties = DatagramBuf.createRequestVector(fbb, propertiesRequests)
  DatagramBuf.startDatagramBuf(fbb)
  DatagramBuf.addRequest(fbb, properties)
  fbb.finish(DatagramBuf.endDatagramBuf(fbb))
  sendWorkerDatagramBuf(fbb.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleSearchPropertiesResponseDatagramBuf(datagram: SearchPropertiesResponseDatagramBuf) {
  onSearchPropertiesResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    search: maybe(parseSearch)(datagram.search()),
    offline: datagram.offline(),
    searchId: parseSearchId(datagram.searchId()!),
  })
}

export function onSearchPropertiesResponse(response: SearchPropertiesResponseDatagram) {
  const { emitEntityEvent, emitInterfaceEvent } = useEvents()

  console.debug("Received search properties", clone(response))

  emitInterfaceEvent("searchPropertiesResponse", response, response.requestId)

  if (response.error)
    return onSearchPropertiesErrored(response.error, response.offline)
  if (!response.search)
    throw new Error("No search in properties response")

  const iSearch = new StaticSearch(response.search)
  emitEntityEvent("searchProperties", iSearch.id, iSearch.inner(), response.offline)
}

function onSearchPropertiesErrored(error: DatagramError, _offline: boolean) {
  const { notifyDatagramError } = useAlerts()

  // FIXME: Currently our reactive search will request the search properties even before it is
  // available, so this will return a spurious error when you do a search
  if (error.type === DatagramErrorTypeBuf.SearchNotFound)
    return console.debug("Search not found", error)

  return notifyDatagramError(error, "Search properties request was unsuccessful")
}
